import { match } from 'ts-pattern';
import { memo } from 'react';
import { COLUMN_SEPARATOR_STYLE, PINNED_LEFT_EDGE_LINE_STYLE, PINNED_RIGHT_EDGE_LINE_STYLE, RESIZE_INDICATOR_LEFT_STYLE, RESIZE_INDICATOR_STYLE } from '../../../../../../constants/borderStyles';
import { DEFAULT_COL_WIDTH } from '../../../../../../constants/defaultColWidth';
import { selectCellOverlayState } from '../../../../../../store/selectors/selectCellOverlayState';
import { useEagGridSelector } from '../../../../../../store/useEagGridSelector';
import type { EagColumn } from '../../../../../../types/EagColumn';
import type { EagColumnState } from '../../../../../../types/EagColumnState';
import type { RowData } from '../../../../../../types/RowData';
import { getValueFromRawCell } from '../../../../../../utils/getValueFromRawCell';
import { getRowPaddingClass, getRowVerticalPaddingPx, LineHeightToPx, type LineHeight } from '../../../../../../utils/lineHeightToValue';
import { renderCell } from '../../../../../../utils/renderCell';

// Drag/resize state is read via a single composite selector (selectCellOverlayState)
// instead of 5 individual selectors.  This cuts subscriptions from 5 × N×M to 1 × N×M.
// verticalSeparators, valueWrapMode, and rowHeight are passed as props from GridRow
// (same for all cells in a row) to avoid per-cell store subscriptions for rarely-changing settings.

export type GridCellProps = {
  column: EagColumn;
  columnState: EagColumnState;
  row: RowData;
  pinnedLeftOffset?: number;
  pinnedRightOffset?: number;
  verticalSeparators: boolean;
  valueWrapMode: string;
  rowHeight: LineHeight;
  pinnedEdgeShadowClass?: string;
  /** Suppress the right-edge column separator on the last data column when an
   * actions column immediately follows — the actions column already renders its
   * own left-edge border, so the two would otherwise form a double line. */
  isLastBeforeActions?: boolean;
};

// memo: rendered N×M times (rows × columns); prevents cascading re-renders from parent.
export const GridCell = memo((props: GridCellProps) => {
  const overlay = useEagGridSelector(selectCellOverlayState);
  const rawValue = props.row[props.column.field];
  const value = getValueFromRawCell(props.column, rawValue);
  const width = props.columnState.width ?? DEFAULT_COL_WIDTH;
  const pinnedStyle = match(props.columnState.pinned).with('LEFT', () => ({
    position: 'sticky' as const,
    left: props.pinnedLeftOffset ?? 0,
    zIndex: 'var(--z-sidebar)'
  })).with('RIGHT', () => ({
    position: 'sticky' as const,
    right: props.pinnedRightOffset ?? 0,
    zIndex: 'var(--z-sidebar)'
  })).otherwise(() => undefined);
  const isPinned = props.columnState.pinned === 'LEFT' || props.columnState.pinned === 'RIGHT';
  const isBeingResized = overlay.isResizing && overlay.resizingColumn === props.column.key;
  const isDragSource = overlay.activeDragId === props.column.key;
  const isDropTarget = overlay.overColumnId === props.column.key && !isDragSource;
  const dragOpacity = isDragSource ? 0.3 : 1;
  const isDropLeft = isDropTarget && overlay.dropSide === 'left';
  const isDropRight = isDropTarget && overlay.dropSide === 'right';
  const bgClass = match({
    isPinned,
    isDropTarget
  }).with({
    isDropTarget: true
  }, () => 'bg-blue-50').with({
    isPinned: true
  }, () => 'bg-white group-hover/row:bg-slate-50').otherwise(() => 'bg-inherit');
  const wrapClass = props.valueWrapMode === 'WRAP' ? 'whitespace-normal break-words' : 'text-ellipsis whitespace-nowrap';
  const paddingClass = getRowPaddingClass[props.rowHeight];
  const rowHeightPx = LineHeightToPx[props.rowHeight];
  // Image height = row height minus the vertical padding applied to the <td>.
  // This ensures the image + its surrounding padding fits exactly within the row.
  const thumbnailHeightPx = rowHeightPx - getRowVerticalPaddingPx[props.rowHeight];
  const shadowClass = props.pinnedEdgeShadowClass ?? '';
  return <td className={`relative overflow-hidden ${paddingClass} ${wrapClass} ${bgClass} ${shadowClass}`} style={{
    width,
    maxWidth: width,
    height: rowHeightPx,
    maxHeight: rowHeightPx,
    ...pinnedStyle,
    opacity: dragOpacity
  }} data-column={props.column.key}>
      {renderCell(props.column, value, props.row, thumbnailHeightPx)}
      {/* Right-pinned columns use left-side indicator to match the left-side resize handle */}
      {isBeingResized && <span style={props.columnState.pinned === 'RIGHT' ? RESIZE_INDICATOR_LEFT_STYLE : RESIZE_INDICATOR_STYLE} />}
      {/* Pinned edge line: full-height separator replacing the 50%-height column separator */}
      {!isBeingResized && props.columnState.pinned === 'LEFT' && <span style={PINNED_LEFT_EDGE_LINE_STYLE} />}
      {!isBeingResized && props.columnState.pinned === 'RIGHT' && <span style={PINNED_RIGHT_EDGE_LINE_STYLE} />}
      {/* Column separator: only on non-pinned cells, and not on the last cell
          before an actions column (which renders its own left-edge border). */}
      {!isBeingResized && props.verticalSeparators && !isPinned && !props.isLastBeforeActions && <span style={COLUMN_SEPARATOR_STYLE} />}
      {isDropLeft && <span className="absolute left-0 top-0 h-full w-px bg-blue-500 z-(--z-page-navigation)" />}
      {isDropRight && <span className="absolute right-0 top-0 h-full w-px bg-blue-500 z-(--z-page-navigation)" />}
    </td>;
});